import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Calendar, MapPin, CaretRight, TrendUp, Toolbox, Megaphone } from "@phosphor-icons/react";
import { api, formatEventDate, splitEventTitle } from "../lib/api";
import Seo from "../components/Seo";

const StatBlock = ({ k, v, sub }) => (
  <div className="hard-border bg-cyber-surface p-6">
    <div className="term-label">{k}</div>
    <div className="font-display font-black text-xl sm:text-2xl mt-2 tracking-tighter text-white leading-[1.1]">{v}</div>
    {sub && <div className="text-white/50 text-[10px] sm:text-[11px] mt-2 font-mono-tech uppercase tracking-[0.08em] whitespace-nowrap overflow-hidden text-ellipsis">{sub}</div>}
  </div>
);

const pad2 = (n) => String(Math.max(0, n)).padStart(2, "0");

const Countdown = ({ date }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const target = new Date(date).getTime();
  const diff = target - now;
  if (isNaN(target) || diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  const units = [
    { v: days, l: "days" },
    { v: hours, l: "hrs" },
    { v: mins, l: "min" },
    { v: secs, l: "sec" },
  ];
  return (
    <div className="inline-flex items-stretch gap-0 hard-border bg-cyber-black/70 backdrop-blur-sm" data-testid="hero-countdown">
      <div className="hidden sm:flex items-center px-4 border-r border-cyber-line">
        <span className="term-label whitespace-nowrap">/ doors open in</span>
      </div>
      {units.map((u, i) => (
        <div key={u.l} className={`px-4 sm:px-5 py-3 text-center ${i > 0 ? "border-l border-cyber-line" : ""}`}>
          <div className="font-mono-tech font-bold text-2xl sm:text-3xl text-cyber-yellow tabular-nums leading-none" data-testid={`countdown-${u.l}`}>
            {u.l === "days" ? u.v : pad2(u.v)}
          </div>
          <div className="font-mono-tech text-[9px] uppercase tracking-[0.3em] text-white/50 mt-1.5">{u.l}</div>
        </div>
      ))}
    </div>
  );
};

const AudienceCard = ({ icon: Icon, pill, title, body }) => (
  <div className="group hard-border bg-cyber-surface p-7 sm:p-8 flex flex-col hover:border-cyber-green transition-colors duration-150" data-testid={`audience-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-center justify-between mb-6">
      <Icon size={28} weight="duotone" className="text-white group-hover:text-cyber-green transition-colors duration-150" />
      <span className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-white/40">{pill}</span>
    </div>
    <h3 className="font-display font-black text-2xl sm:text-3xl leading-[1.05] tracking-tighter text-white">{title}</h3>
    <p className="text-white/60 text-sm mt-4 leading-relaxed flex-1">{body}</p>
  </div>
);

const NewsTeaser = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/news", { params: { limit: 3 } }).then(({ data }) => setItems(data.slice(0, 3))).catch(() => {});
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="border-t border-cyber-line bg-cyber-black" data-testid="newsroom-teaser">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="term-label mb-3">/ from the newsroom</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Signal, not noise.</h2>
          </div>
          <Link to="/news" className="btn-ghost-neon" data-testid="newsroom-cta">All articles <ArrowUpRight size={14} weight="bold" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {items.map((a) => (
            <Link key={a.id} to={`/news/${a.slug}`} className="group hard-border bg-cyber-surface overflow-hidden flex flex-col" data-testid={`teaser-${a.slug}`}>
              {a.hero_image && (
                <div className="aspect-[16/10] overflow-hidden bg-cyber-black">
                  <img
                    src={a.hero_image}
                    alt={a.title}
                    className="w-full h-full object-cover img-grey"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
              )}
              <div className="p-6 flex-1 flex flex-col">
                <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-cyber-green">{a.category}</div>
                <h3 className="font-display font-black tracking-tighter text-2xl mt-3 leading-[1.05] line-clamp-3 group-hover:text-white/90 transition-colors">{a.title}</h3>
                <p className="text-white/60 text-sm mt-3 line-clamp-2 flex-1">{a.excerpt}</p>
                <div className="mt-5 flex items-center justify-between border-t border-cyber-line pt-4">
                  <span className="font-mono-tech text-[10px] text-white/40 uppercase tracking-[0.2em]">{formatEventDate(a.published_at)} · {a.read_time_min}m</span>
                  <ArrowUpRight size={14} weight="bold" className="text-white group-hover:rotate-45 transition-transform duration-150" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

const TrustedSponsors = ({ event }) => {
  const [failed, setFailed] = useState({});
  if (!event || !Array.isArray(event.sponsors) || event.sponsors.length === 0) return null;
  return (
    <section className="border-t border-cyber-line bg-cyber-black" data-testid="trusted-sponsors">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
        <div className="max-w-4xl">
          <div className="term-label mb-4">/ trusted by</div>
          <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl lg:text-5xl leading-[1.05]">
            Leading FinTech marketing brands trust{" "}
            <span className="text-cyber-yellow">FintechOmni</span> to grow their business.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {event.sponsors.map((s, i) => {
            const showLogo = s.logo && !failed[i];
            return (
              <a
                key={`${s.name}-${i}`}
                href={s.website || "#"}
                target={s.website ? "_blank" : undefined}
                rel="noreferrer"
                className="group bg-white flex items-center justify-center aspect-[3/2] p-5 sm:p-7 transition-all duration-150 border border-white/10 hover:border-cyber-green"
                data-testid={`trusted-sponsor-${i}`}
                title={s.name}
              >
                {showLogo ? (
                  <img
                    src={s.logo}
                    alt={s.name}
                    className="w-full h-full object-contain transition-transform duration-150 group-hover:scale-[1.04]"
                    onError={() => setFailed((f) => ({ ...f, [i]: true }))}
                    loading="lazy"
                  />
                ) : (
                  <div className="font-display font-black text-center text-[#0b1024] text-base sm:text-lg leading-tight">
                    {s.name}
                  </div>
                )}
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Landing = () => {
  const [featured, setFeatured] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events/featured").then(({ data }) => setFeatured(data)).catch(() => {});
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  return (
    <main className="grain" data-testid="landing-page">
      <Seo
        path="/"
        title="Private events for fintech marketing leaders"
        description="Where fintech marketing leaders meet. Invite-only gatherings for performance marketing operators — sub-20% approval rate, no panels, no filler."
        keywords="fintech marketing events, fintech conferences, performance marketing, fintech networking, fintech CMO, BNPL marketing, lending marketing, fintech sponsors"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": "FintechOmni",
          "url": "https://fintechomni.com",
          "potentialAction": {
            "@type": "SearchAction",
            "target": "https://fintechomni.com/news?q={search_term_string}",
            "query-input": "required name=search_term_string"
          }
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-cyber-line min-h-[88vh] flex items-center">
        {/* Background video */}
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster="https://images.unsplash.com/photo-1614487331144-b5b141c11eab?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920"
          src="https://customer-assets.emergentagent.com/job_omni-rsvp-portal/artifacts/uy9apte2_FinTechOmni%20Sizzle%20Reel%20%281%29.mov"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="hero-video"
        />
        {/* Overlays: deep navy tint + vignette + scanlines */}
        <div className="absolute inset-0 bg-cyber-black/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-cyber-black/30 via-cyber-black/55 to-cyber-black" />
        <div className="scanlines absolute inset-0 pointer-events-none" />
        <div className="marquee-bg absolute inset-x-0 bottom-10 flex flex-col justify-center pointer-events-none mix-blend-screen">
          <Marquee gradient={false} speed={32} className="opacity-50">
            <div className="font-display font-black tracking-tighter text-[20vw] sm:text-[16vw] outline-text whitespace-nowrap pr-8">
              FINTECH MARKETING&nbsp;//&nbsp;FINTECH MARKETING&nbsp;//&nbsp;
            </div>
          </Marquee>
        </div>

        <div className="relative w-full max-w-[1400px] mx-auto px-5 sm:px-8 pt-14 sm:pt-28 pb-16 sm:pb-32">
          <div className="flex items-center gap-3 mb-6 sm:mb-8">
            <span className="w-2 h-2 bg-cyber-green animate-blink" />
            <span className="term-label">/ FINTECHOMNI — Private Event</span>
          </div>

          <h1 className="font-display font-black tracking-tighter text-[44px] sm:text-6xl lg:text-8xl leading-[0.95] sm:leading-[0.92] max-w-6xl drop-shadow-[0_2px_30px_rgba(4,8,26,0.9)]">
            Where fintech marketing <br />
            <span className="text-cyber-yellow">leaders meet.</span>
          </h1>

          <p className="mt-6 sm:mt-8 max-w-2xl text-base sm:text-lg text-white/80 leading-relaxed drop-shadow-[0_2px_8px_rgba(4,8,26,0.9)]">
            <strong className="text-white">Private. Curated. Invitation Only.</strong><br />
            No presentations. No booths. No sales pitches. Just fintech marketing leaders building relationships, sharing ideas, and making deals.
          </p>

          {/* Countdown hidden until 30 days before the event */}
          {featured && !featured.date_tbd && featured.date &&
            new Date(featured.date).getTime() - Date.now() <= 30 * 86400000 && (
            <div className="mt-8 sm:mt-10">
              <div className="sm:hidden term-label mb-2">/ doors open in</div>
              <Countdown date={featured.date} />
            </div>
          )}

          <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
            {featured && (
              <Link to={`/events/${featured.slug}/rsvp`} className="btn-neon justify-center" data-testid="hero-rsvp-cta">
                Request an Invitation <ArrowUpRight size={16} weight="bold" /> {featured.title.split(' ').slice(0, 3).join(' ')}
              </Link>
            )}
          </div>

          {/* Bento ticker */}
          <div className="mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
            <StatBlock k="/ events / year" v="USA · LATAM" sub="Vegas · NY · San Juan" />
            <StatBlock k="/ attendees" v="Marketing Pros" sub="curated guest list" />
            <StatBlock k="/ format" v="Mixer" sub="Private Event" />
            <StatBlock k="/ since" v="2023" sub="growing fast" />
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="border-b border-cyber-line bg-cyber-black" data-testid="audience-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <div className="term-label mb-5">/ who this event is for</div>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-8xl leading-[0.9]">
              FINTECHOMNI
            </h2>
            <p className="mt-8 text-white/75 text-xl sm:text-2xl lg:text-[26px] leading-[1.35] font-display font-medium max-w-3xl mx-auto">
              This is a closed-room gathering for the people running, enabling, and buying performance marketing in fintech &mdash; the teams behind millions of leads, approvals, and renewals.
            </p>
          </div>

          {/* Three audience cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <AudienceCard
              icon={TrendUp}
              pill="/ 01"
              title="Performance Marketers"
              body="Omni-channel media buyers and marketers running acquisition, retention, and brand for fintech products."
            />
            <AudienceCard
              icon={Toolbox}
              pill="/ 02"
              title="Solution Providers"
              body="Lead gen networks, compliance tooling, identity + fraud, attribution platforms, and tech vendors powering the fintech credit stack."
            />
            <AudienceCard
              icon={Megaphone}
              pill="/ 03"
              title="Advertisers"
              body="Brand teams, marketing directors, and CMOs at lenders, issuers, and credit product companies investing in demand generation at scale."
            />
          </div>

          {/* Product categories */}
          <div className="mt-12 hard-border p-6 sm:p-8 bg-cyber-surface" data-testid="product-categories">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6">
              <div className="lg:w-56 shrink-0">
                <div className="term-label mb-2">/ verticals covered</div>
                <div className="font-display font-black text-2xl leading-tight">Consumer &amp; Business Fintech Products.</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  "Personal loans",
                  "Installment & short-term lending",
                  "BNPL (Buy Now Pay Later)",
                  "MCA (Merchant Cash Advance)",
                  "Credit building & monitoring",
                  "Checking/savings apps",
                  "Debit & Credit cards",
                  "Debt Consolidation & Refinance",
                  "Insurtech",
                  "Compliance",
                  "Anti-Fraud",
                  "Data Validation",
                ].map((tag) => (
                  <span
                    key={tag}
                    className="font-mono-tech text-xs uppercase tracking-[0.15em] text-white border border-cyber-line px-3 py-2 hover:border-cyber-green hover:text-cyber-green transition-colors duration-150"
                    data-testid={`vertical-${tag.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED EVENT */}
      {featured && (
        <section className="border-b border-cyber-line bg-cyber-black" data-testid="featured-event">
          <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="term-label mb-4">/ next up</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.95]">
                {splitEventTitle(featured.title).map((line, i) => (
                  <span key={`${line}-${i}`} className="block">{line}</span>
                ))}
              </h2>
              <p className="mt-5 text-white/70 text-lg leading-relaxed">{featured.tagline}</p>
              <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
                <div className="hard-border p-4">
                  <div className="term-label flex items-center gap-2"><Calendar size={12} /> date</div>
                  <div className="mt-2 font-mono-tech text-cyber-yellow">{formatEventDate(featured.date)}</div>
                </div>
                <div className="hard-border p-4">
                  <div className="term-label flex items-center gap-2"><MapPin size={12} /> location</div>
                  <div className="mt-2 font-mono-tech text-cyber-yellow">{featured.city || featured.location}</div>
                </div>
              </div>
              <Link to={`/events/${featured.slug}`} className="btn-neon mt-8" data-testid="featured-cta">
                See Event Details <CaretRight weight="bold" size={14} />
              </Link>
            </div>
            <div className="lg:col-span-7 group relative aspect-[4/3] overflow-hidden hard-border">
              {featured.hero_image && (
                <img src={featured.hero_image} alt={featured.title} className="w-full h-full object-cover" />
              )}
              <div className="absolute inset-0 bg-gradient-to-r from-cyber-black via-cyber-black/55 to-transparent pointer-events-none" />
              <div className="absolute top-4 left-4 px-3 py-1 bg-white text-cyber-black font-mono-tech text-[10px] tracking-[0.25em] uppercase">FEATURED</div>
              <div className="absolute bottom-4 right-4 px-3 py-1 bg-cyber-black/80 border border-white text-white font-mono-tech text-[10px] tracking-[0.25em] uppercase">{featured.venue}</div>
            </div>
          </div>
        </section>
      )}

      {/* ALL EVENTS — hidden for now; single-event focus
      <section className="bg-cyber-black" data-testid="events-grid-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="term-label mb-3">/ all events</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl">Upcoming sessions.</h2>
            </div>
            <div className="font-mono-tech text-white/50 text-xs">{events.length.toString().padStart(2, '0')} events listed</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {events.map((e, idx) => (
              <Link
                key={e.id}
                to={`/events/${e.slug}`}
                className={`group hard-border bg-cyber-surface relative overflow-hidden ${idx % 3 === 0 ? 'md:col-span-2' : ''}`}
                data-testid={`event-card-${e.slug}`}
              >
                <div className="grid grid-cols-12 gap-0">
                  {e.hero_image && (
                    <div className="col-span-12 md:col-span-5 aspect-[4/3] md:aspect-auto overflow-hidden">
                      <img src={e.hero_image} alt={e.title} className="w-full h-full object-cover img-grey" />
                    </div>
                  )}
                  <div className={`p-6 sm:p-8 flex flex-col justify-between ${e.hero_image ? 'col-span-12 md:col-span-7' : 'col-span-12'}`}>
                    <div>
                      <div className="term-label">{e.date_tbd ? "Date TBD" : formatEventDate(e.date)} · {e.city || e.location}</div>
                      <h3 className="font-display font-black tracking-tighter text-3xl sm:text-4xl mt-3 group-hover:text-cyber-yellow transition-colors duration-150">
                        {e.title}
                      </h3>
                      <p className="text-white/60 mt-3 line-clamp-3">{e.tagline || e.description}</p>
                    </div>
                    <div className="mt-6 flex items-center justify-between">
                      <span className="font-mono-tech text-xs text-cyber-green tracking-[0.2em] uppercase">INVITATIONS_OPEN</span>
                      <ArrowUpRight size={20} className="text-cyber-yellow group-hover:rotate-45 transition-transform duration-150" weight="bold" />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* MANIFESTO */}
      <section className="border-t border-cyber-line bg-cyber-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="term-label">/ manifesto</div>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-display font-black tracking-tighter text-3xl sm:text-5xl leading-[1.05]">
              We are tired of <span className="line-through text-white/30">fintech panels</span>.
              <br />
              We host <span className="text-cyber-green">conversations.</span>
            </h2>
            <p className="text-white/70 text-lg mt-6 max-w-2xl leading-relaxed">
              Every guest is curated. Invitations are reviewed by hand because the room matters more than the headcount.
            </p>
          </div>
        </div>
      </section>

      {/* TRUSTED-BY LOGO STRIP */}
      <TrustedSponsors event={featured} />

      {/* NEWSROOM TEASER */}
      <NewsTeaser />
    </main>
  );
};

export default Landing;
